import {
  Component,
  computed,
  contentChild,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';

import { MbscDatepickerModule, localeDe, MbscDatepicker } from '@mobiscroll/angular';
import { I18nModule } from '../../../i18n/i18n.module';
import { DateService, equals, Store } from '@softline/core';
import { DateParser } from '../../../services/date.parser';
import { SOFTLINE_FEATURE_MODAL } from '../../../modal/legacy/modal.shared';
import * as ModalStore from '../../../modal/legacy/modal.store';
import { DateRangeFieldInput } from '../../form/date-range-input/date-range-field-input';
import { DateRange } from '../../../modal/types/date-range';

export const defaultDateRangeShortcuts: { value: DateRange; label: string }[] = [
  {
    label: 'Gestern',
    value: {from: 'h-1', to: 'h-1'}
  }, {
    label: 'Diese Woche',
    value: {from: 'mo', to: 'h'}
  },
  {
    label: 'Dieses Monat',
    value: {from: 'ma', to: 'h'}
  },
  {
    label: 'Letzte Woche',
    value: {from: 'mo-1', to: 'so-1'}
  },
  {
    label: 'Letztes Monat',
    value: {from: 'ma-1', to: 'me-1'}
  },
  {
    label: 'Letzten 7 Tage',
    value: {from: 'h-7', to: 'h'}
  },
  {
    label: 'Letzten 30 Tage',
    value: {from: 'h-30', to: 'h'}
  }
];
export const longDateRangeShortcuts: { value: DateRange; label: string }[] = [
  {
    label: 'Diese Woche',
    value: {from: 'mo', to: 'h-1'}
  }, {
    label: 'Dieses Monat',
    value: {from: 'ma', to: 'h'}
  },
  {
    label: 'Dieses Jahr',
    value: {from: 'ja', to: 'h'}
  },
  {
    label: 'Letzte Woche',
    value: {from: 'mo-1', to: 'so-1'}
  },
  {
    label: 'Letztes Monat',
    value: {from: 'ma-1', to: 'me-1'}
  },
  {
    label: 'Letztes Jahr',
    value: {from: 'ja-1', to: 'je-1'}
  },
  {
    label: 'Letzten 7 Tage',
    value: {from: 'h-7', to: 'h'}
  },
  {
    label: 'Letzten 30 Tage',
    value: {from: 'h-30', to: 'h'}
  },
];
export const referenceDateRangeShortcuts: (reference: DateRange) => { value: DateRange; label: string }[] = (reference: DateRange) => {
  return [];
  //TODO
  /*const from = reference.from;
  const to = reference.to;
  const daysBetween = to.diff(from, 'days');
  const referenceTo = from.clone().add(-1, 'day');
  const referenceFrom = referenceTo.clone().add(-daysBetween, 'day');

  const periodYearFrom = moment().startOf('year').add(-1, 'year');
  const periodYearTo = periodYearFrom.clone().add(daysBetween, 'day');

  return [
    {
      label: 'Vorperiode',
      value: {from: referenceFrom.format('YYYY-MM-DD'), to: referenceTo.format('YYYY-MM-DD')}
    },
    {
      label: 'Letzte Woche',
      value: {from: 'mo-1', to: 'so-1'}
    },
    {
      label: 'Letztes Monat',
      value: {from: 'ma-1', to: 'me-1'}
    },
    {
      label: 'Letztes Jahr',
      value: {from: 'ja-1', to: 'je-1'}
    },
    {
      label: 'Letztes Jahr (Periode)',
      value: {from: 'ja-1', to: periodYearTo.format('YYYY-MM-DD')}
    }
  ]*/
}

@Component({
    selector: 'soft-date-range-picker',
    imports: [I18nModule, MbscDatepickerModule],
    templateUrl: './date-range-picker.component.html',
    styleUrl: './date-range-picker.component.css'
})
export class DateRangePickerComponent {
  public localeDe = localeDe;

  dateService = inject(DateService);
  dateParser = inject<DateParser<string>>(DateParser);
  store = inject(Store);

  value = signal<DateRange | null>(null);
  valueChange = output<DateRange | null>();
  valueInput = input<DateRange | null>(null, {alias: 'value'});
  valueEffect = effect(() => {
    this.value.set(this.valueInput());
  })
  readonly shortcuts = input<{value: DateRange, label: string}[] | null>(null);
  readonly defaultShortcuts = defaultDateRangeShortcuts;

  valueArray = computed(() => {
    const value = this.value();
    return value ? [value.from, value.to] : [null, null];
  });
  selectShortcut = output<DateRange>();

  startField = input<'from' | 'to'>('from');

  datePicker = viewChild<MbscDatepicker>('datepicker');
  datePickerEffect = effect(() => {
    const datePicker = this.datePicker();
    const value = untracked(() => this.value());
    const startField = untracked(() => this.startField());
    if(datePicker && value?.from && startField === 'to') {
      datePicker._activeSelect = 'end';
    }
  })

  constructor() { }

  onCellClick(date: string[]): void {
    this.valueChange.emit({from: date[0], to: date[1]});
  }

  onValueChange(valueArray: [string | null, string | null]): void {
    const from = valueArray[0];
    const to = valueArray[1];
    if(!from || !to)
      return;
    const value: DateRange = {from, to};
    const currentValue = this.value();
    if(equals(currentValue, value))
      return;

    const datePicker = this.datePicker();
    if(datePicker && value.from)
      datePicker._activeSelect = 'end';

    this.value.set(value);
    this.valueChange.emit(value);
  }


  onSelectShortcut(shortcut:  { value: DateRange; label: string }): void {
    const from = shortcut.value.from ? this.dateParser.parse(shortcut.value.from) : null;
    const to = shortcut.value.to ? this.dateParser.parse(shortcut.value.to) : null;

    if(!from || !to)
      return;
    const value: DateRange = {from, to};
    this.value.set(value);
    this.valueChange.emit(value);
    this.selectShortcut.emit(value);
  }

  async openShortcutsList(): Promise<void> {
    const shortcuts = this.shortcuts();
    if(!shortcuts)
      return;
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.choose<DateRange>(),
      {
        title: 'Shortcuts',
        dismiss: true,
        options: shortcuts.map(o => ({
          value: o.value,
          label: o.label
        }))
      }
    );

    if (!result || result === 'DISMISSED')
      return;

    const shortcut = shortcuts.find(o => o.value === result);

    if (!shortcut)
      return;

    this.onSelectShortcut(shortcut);
  }
}
